import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ParsedRow } from './csvUtils';

export type DuplicadoAction = 'atualizar' | 'ignorar';

export interface DuplicadoInfo {
  row: ParsedRow;
  existing: { id: string; nome: string; carreira: string; nivel_atual: number; vencimento: number };
  action: DuplicadoAction;
}

interface DuplicadosStepProps {
  rows: ParsedRow[];
  municipioId: string | null;
  onConfirm: (duplicados: DuplicadoInfo[], novos: ParsedRow[]) => void;
  onBack: () => void;
}

export default function DuplicadosStep({ rows, municipioId, onConfirm, onBack }: DuplicadosStepProps) {
  const [loading, setLoading] = useState(true);
  const [duplicados, setDuplicados] = useState<DuplicadoInfo[]>([]);
  const [novos, setNovos] = useState<ParsedRow[]>([]);
  const [globalAction, setGlobalAction] = useState<DuplicadoAction | null>(null);

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      const ids = rows.map((r) => r.id).filter((id) => !id.startsWith('AUTO-'));
      if (ids.length === 0) {
        setNovos(rows);
        setDuplicados([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('funcionarios')
        .select('id, nome, carreira, nivel_atual, vencimento')
        .in('id', ids);

      if (error || !data) {
        setNovos(rows);
        setDuplicados([]);
        setLoading(false);
        return;
      }

      const existingMap = new Map(data.map((d) => [d.id, d]));
      const dups: DuplicadoInfo[] = [];
      const newRows: ParsedRow[] = [];

      for (const row of rows) {
        const existing = existingMap.get(row.id);
        if (existing) {
          dups.push({ row, existing, action: 'atualizar' });
        } else {
          newRows.push(row);
        }
      }

      setDuplicados(dups);
      setNovos(newRows);
      setLoading(false);
    };
    check();
  }, [rows, municipioId]);

  const setAction = (idx: number, action: DuplicadoAction) => {
    setDuplicados((prev) => prev.map((d, i) => i === idx ? { ...d, action } : d));
    setGlobalAction(null);
  };

  const applyGlobal = (action: DuplicadoAction) => {
    setGlobalAction(action);
    setDuplicados((prev) => prev.map((d) => ({ ...d, action })));
  };

  const toUpdate = duplicados.filter((d) => d.action === 'atualizar');
  const toIgnore = duplicados.filter((d) => d.action === 'ignorar');

  if (loading) {
    return (
      <div className="p-10 flex flex-col items-center gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-2xl">
          <i className="ri-loader-4-line text-zinc-400 text-2xl animate-spin" />
        </div>
        <p className="text-[13px] text-zinc-500">A verificar duplicados na base de dados...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Resumo */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-zinc-50 rounded-xl p-3 text-center">
          <p className="text-[22px] font-bold text-zinc-700">{rows.length}</p>
          <p className="text-[11px] text-zinc-500 font-medium">Total no CSV</p>
        </div>
        <div className={`rounded-xl p-3 text-center ${duplicados.length > 0 ? 'bg-amber-50' : 'bg-zinc-50'}`}>
          <p className={`text-[22px] font-bold ${duplicados.length > 0 ? 'text-amber-700' : 'text-zinc-400'}`}>
            {duplicados.length}
          </p>
          <p className={`text-[11px] font-medium ${duplicados.length > 0 ? 'text-amber-600' : 'text-zinc-400'}`}>
            Já existem na BD
          </p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center">
          <p className="text-[22px] font-bold text-emerald-700">{novos.length}</p>
          <p className="text-[11px] text-emerald-600 font-medium">Novos registos</p>
        </div>
      </div>

      {duplicados.length === 0 ? (
        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <i className="ri-checkbox-circle-fill text-emerald-500 text-lg flex-shrink-0" />
          <div>
            <p className="text-[13px] font-semibold text-emerald-800">Sem duplicados!</p>
            <p className="text-[12px] text-emerald-700 mt-0.5">
              Todos os {rows.length} funcionários são novos registos. Podes avançar para a importação.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <i className="ri-alert-fill text-amber-500 text-base flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-amber-800">
                {duplicados.length} funcionário(s) já existem na base de dados
              </p>
              <p className="text-[12px] text-amber-700 mt-0.5">
                Decide para cada um se queres atualizar os dados ou ignorar.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => applyGlobal('atualizar')}
                className={`whitespace-nowrap text-[11px] font-semibold px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
                  globalAction === 'atualizar'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
              >
                Atualizar todos
              </button>
              <button
                onClick={() => applyGlobal('ignorar')}
                className={`whitespace-nowrap text-[11px] font-semibold px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
                  globalAction === 'ignorar'
                    ? 'bg-zinc-700 text-white'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                Ignorar todos
              </button>
            </div>
          </div>

          {/* Lista de duplicados */}
          <div className="border border-zinc-100 rounded-xl overflow-hidden">
            <div className="bg-zinc-50 px-4 py-2.5 border-b border-zinc-100 grid grid-cols-12 gap-2">
              <span className="col-span-1 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Nº</span>
              <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Nome</span>
              <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Dados atuais</span>
              <span className="col-span-3 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Dados do CSV</span>
              <span className="col-span-2 text-[10px] font-semibold text-zinc-400 uppercase tracking-widest text-right">Ação</span>
            </div>
            <div className="divide-y divide-zinc-50 max-h-64 overflow-y-auto">
              {duplicados.map((dup, idx) => (
                <div key={idx} className={`px-4 py-3 grid grid-cols-12 gap-2 items-center ${dup.action === 'ignorar' ? 'opacity-50' : ''}`}>
                  <span className="col-span-1 text-[11px] font-mono text-zinc-400">{dup.row.id}</span>
                  <div className="col-span-3">
                    <p className="text-[12px] font-medium text-zinc-800 truncate">{dup.existing.nome}</p>
                  </div>
                  <div className="col-span-3 space-y-0.5">
                    <p className="text-[10px] text-zinc-500">{dup.existing.carreira}</p>
                    <p className="text-[10px] text-zinc-400">Nível {dup.existing.nivel_atual} · {dup.existing.vencimento}€</p>
                  </div>
                  <div className="col-span-3 space-y-0.5">
                    <p className={`text-[10px] ${dup.row.carreira !== dup.existing.carreira ? 'text-amber-600 font-semibold' : 'text-zinc-500'}`}>
                      {dup.row.carreira}
                    </p>
                    <p className={`text-[10px] ${(dup.row.nivel_atual !== dup.existing.nivel_atual || dup.row.vencimento !== dup.existing.vencimento) ? 'text-amber-600 font-semibold' : 'text-zinc-400'}`}>
                      Nível {dup.row.nivel_atual} · {dup.row.vencimento > 0 ? `${dup.row.vencimento}€` : '—'}
                    </p>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button
                      onClick={() => setAction(idx, 'atualizar')}
                      className={`whitespace-nowrap text-[10px] font-semibold px-2 py-1 rounded-md cursor-pointer transition-colors ${
                        dup.action === 'atualizar'
                          ? 'bg-amber-500 text-white'
                          : 'bg-zinc-100 text-zinc-500 hover:bg-amber-100 hover:text-amber-700'
                      }`}
                    >
                      Atualizar
                    </button>
                    <button
                      onClick={() => setAction(idx, 'ignorar')}
                      className={`whitespace-nowrap text-[10px] font-semibold px-2 py-1 rounded-md cursor-pointer transition-colors ${
                        dup.action === 'ignorar'
                          ? 'bg-zinc-600 text-white'
                          : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'
                      }`}
                    >
                      Ignorar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Resumo das ações */}
          <div className="flex items-center gap-3 text-[12px] text-zinc-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              {toUpdate.length} a atualizar
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-zinc-300" />
              {toIgnore.length} a ignorar
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {novos.length} novos
            </span>
          </div>
        </>
      )}

      {/* Rodapé */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onBack}
          className="whitespace-nowrap flex items-center gap-2 text-[13px] text-zinc-500 hover:text-zinc-800 border border-zinc-200 px-4 py-2 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-sm" />
          Voltar
        </button>
        <button
          onClick={() => onConfirm(duplicados, novos)}
          className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
        >
          Avançar para importação
          <i className="ri-arrow-right-line text-sm" />
        </button>
      </div>
    </div>
  );
}
